
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';

export function LuxuryWhiteGoldHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-white text-gray-800 font-serif">
      <header className="container mx-auto flex justify-between items-center p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-bold tracking-[0.2em] uppercase text-amber-900">
          AETOS
        </h2>
        <nav className="hidden md:flex gap-8 text-sm uppercase tracking-wider text-amber-800">
          <a href="#" className="hover:text-amber-600">
            The Collection
          </a>
          <a href="#" className="hover:text-amber-600">
            Our Legacy
          </a>
          <a href="#" className="hover:text-amber-600">
            Bespoke
          </a>
        </nav>
        <div className="md:hidden">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="text-amber-900" /> : <Menu className="text-amber-900" />}
          </Button>
        </div>
      </header>
       {isMenuOpen && (
        <div className="md:hidden bg-white/95 border-y">
          <nav className="container mx-auto flex flex-col items-start gap-4 p-6 text-lg uppercase tracking-wider text-amber-800">
             <a href="#" className="hover:text-amber-600">The Collection</a>
             <a href="#" className="hover:text-amber-600">Our Legacy</a>
             <a href="#" className="hover:text-amber-600">Bespoke</a>
          </nav>
        </div>
      )}
      <main>
        <section className="bg-[#FBF9F5] py-16 md:py-32 px-4">
          <div className="container mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h1 className="text-5xl md:text-7xl font-bold text-amber-950 leading-tight">
                Timeless by Design
              </h1>
              <p className="mt-4 text-xl text-amber-900 max-w-lg mx-auto md:mx-0">
                Crafting exquisite timepieces for generations. A heritage of
                precision, an art of elegance.
              </p>
              <Button className="mt-8 bg-amber-800 text-white hover:bg-amber-900 rounded-none uppercase tracking-widest px-10 py-7 text-sm font-bold">
                Discover the Craft
              </Button>
            </div>
            <div className="relative h-[60vh] w-full">
              <Image
                src="https://picsum.photos/seed/goldwatch1/1000/1200"
                alt="A close-up of a luxurious gold watch on a white marble background"
                fill
                className="object-cover"
                priority
                data-ai-hint="gold watch"
              />
            </div>
          </div>
        </section>

        <section className="py-24 px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl font-bold text-amber-950">
                The Aetos Difference
              </h2>
              <p className="mt-4 text-lg text-amber-900">
                More than a watch, it is a statement. A fusion of masterful
                engineering and aesthetic perfection.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-12 text-center">
              <div>
                <div className="w-16 h-16 mx-auto mb-4 border-2 border-amber-800 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-amber-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-amber-950">
                  Swiss Movement
                </h3>
                <p className="text-amber-800 mt-2">
                  At the heart of every AETOS watch lies a meticulously
                  calibrated, in-house Swiss automatic movement.
                </p>
              </div>
              <div>
                <div className="w-16 h-16 mx-auto mb-4 border-2 border-amber-800 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-amber-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-amber-950">
                  Precious Materials
                </h3>
                <p className="text-amber-800 mt-2">
                  We use only 18k gold, flawless diamonds, and sapphire
                  crystal, ensuring enduring beauty and resilience.
                </p>
              </div>
              <div>
                <div className="w-16 h-16 mx-auto mb-4 border-2 border-amber-800 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-amber-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m5-4h1m-1 4h1"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-amber-950">
                  Hand-Finished
                </h3>
                <p className="text-amber-800 mt-2">
                  Each component is polished and assembled by hand in our
                  Geneva atelier by master watchmakers.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#FBF9F5] py-24 px-4">
          <div className="container mx-auto grid md:grid-cols-2 gap-16 items-center">
            <div className="relative h-[80vh]">
              <Image
                src="https://picsum.photos/seed/goldwatch2/1000/1500"
                alt="Detailed shot of a watchmaker assembling a timepiece"
                fill
                className="object-cover"
                data-ai-hint="watchmaker hands"
              />
            </div>
            <div className="md:pl-8">
              <h2 className="text-4xl font-bold text-amber-950">
                A Legacy of Excellence
              </h2>
              <p className="mt-4 text-lg text-amber-900">
                Since 1888, AETOS has been synonymous with horological
                innovation. We honor our past by creating the heirlooms of the
                future.
              </p>
              <Button
                variant="link"
                className="mt-6 p-0 text-amber-800 text-lg hover:text-amber-600"
              >
                Explore Our History
              </Button>
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-amber-900 text-white py-16 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Request a Catalogue</h2>
          <p className="max-w-xl mx-auto text-amber-100 mb-8">
            Experience the world of AETOS. Request our latest catalogue for a
            private viewing of the collection.
          </p>
          <Button
            variant="outline"
            className="border-amber-200 text-amber-100 hover:bg-amber-100 hover:text-amber-900 rounded-none uppercase tracking-widest px-10 py-7 text-sm font-bold"
          >
            Inquire
          </Button>
          <div className="mt-16 pt-8 border-t border-amber-800 flex flex-col md:flex-row justify-between items-center text-sm text-amber-300 gap-4">
            <p>
              &copy; {new Date().getFullYear()} AETOS SA. All Rights Reserved.
            </p>
            <p>Geneva, Switzerland</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
